import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { ApiError } from "../../api/errors";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
  deleteTheme, effectiveThemeBackground, effectiveThemeLogo, listThemes, publishTheme, savedThemeBackground, savedThemeLogo, saveTheme,
  MAX_THEMES, THEME_NAME, type EffectiveTheme, type SavedTheme,
} from "../../api/themes";
import { extractBrand, type BrandColours } from "../../domain/logoColours";
import { variationsFor } from "../../domain/schemes";
import { checkLogo, MAX_THEME_FILE_BYTES, PAGE_DEFAULTS, parseThemeFile } from "../../domain/themeFile";
import type { PortalLayout, PortalLogoSize } from "../../portal/api";
import { deriveTokens, type ThemeTokens } from "../../portal/palette";
import { describeError } from "./errors";
import { readLogo, type LogoRead } from "./image";
import { PreviewDialog, type PreviewSubject } from "./PreviewDialog";
import { ThemeEditor, type EditorStart } from "./ThemeEditor";
import { useOperatorDocuments } from "./useOperatorDocuments";
import { usePortalContact } from "./usePortalContact";

/** Schemes shown at first, and added by each "More variations". */
const PAGE = 3;

/**
 * Portal appearance: upload a logo, pick a scheme -- three at first, more on
 * request from the same logo -- save it as a named theme, publish one. The
 * captive portal renders the published theme on its next load; the guest URL
 * never changes.
 *
 * Every variation, every saved theme (published or not) and the live theme
 * opens in a full preview: the real portal page, painted with that theme. A
 * variation or a saved theme opens in the editor (colours and every text), and
 * a theme file downloaded from the editor opens there again.
 *
 * Design and contract: radius-wiki portal-brand-from-logo.md.
 */

function Swatches({ colours }: { colours: Array<[string, string]> }) {
  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
      {colours.map(([label, c]) => (
        <Stack key={label} direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
          <Box sx={{ width: 22, height: 22, borderRadius: 1, bgcolor: c, border: 1, borderColor: "divider" }} />
          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>{label} {c}</Typography>
        </Stack>
      ))}
    </Stack>
  );
}

/** A miniature of the portal's login screen, painted with the portal's own tokens. */
function PortalPreview({ tokens: t, accent, logoUrl, title }: { tokens: ThemeTokens; accent: string; logoUrl: string | null; title: string }) {
  return (
    <Box sx={{ borderRadius: 2, overflow: "hidden", border: 1, borderColor: "divider" }}>
      <Box sx={{ bgcolor: t["--stage"], p: 1.5, textAlign: "center" }}>
        {logoUrl && <Box component="img" src={logoUrl} alt="" sx={{ maxHeight: 44, maxWidth: "70%", borderRadius: 1, display: "block", mx: "auto", mb: 1 }} />}
        <Typography sx={{ color: t["--stage-fg"], fontWeight: 700, fontSize: 15 }}>{title}</Typography>
        <Typography sx={{ color: t["--stage-sub"], fontSize: 12 }}>Sign in to get online</Typography>
      </Box>
      <Box sx={{ bgcolor: t["--surface"], p: 1.5 }}>
        <Typography sx={{ color: t["--text"], fontSize: 12, fontWeight: 600, mb: 0.5 }}>Registered mobile number</Typography>
        <Box sx={{ border: 1, borderColor: t["--border-strong"], borderRadius: 1, height: 26, mb: 1 }} />
        <Box sx={{ bgcolor: accent, color: t["--on-accent"], borderRadius: 1, py: 0.75, textAlign: "center", fontWeight: 700, fontSize: 14 }}>
          Connect
        </Box>
        <Typography sx={{ color: t["--link"], fontSize: 13, fontWeight: 600, textAlign: "center", mt: 1 }}>Forgot password?</Typography>
      </Box>
    </Box>
  );
}

/** A stored theme's page settings, as the preview frame takes them. */
function pageOf(t: { backdrop?: string; layout?: PortalLayout; card_opacity?: number; logo_size?: PortalLogoSize }) {
  return {
    ...(t.backdrop ? { backdrop: t.backdrop } : {}),
    ...(t.layout ? { layout: t.layout } : {}),
    ...(t.card_opacity !== undefined ? { cardOpacity: t.card_opacity } : {}),
    ...(t.logo_size ? { logoSize: t.logo_size } : {}),
  };
}

function useBlobUrl(load: (() => Promise<Blob>) | null, key: string): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!load) {
      setUrl(null);
      return;
    }
    let live = true;
    let made: string | null = null;
    load()
      .then((b) => {
        if (!live) return;
        made = URL.createObjectURL(b);
        setUrl(made);
      })
      .catch(() => live && setUrl(null));
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
    // The key names the bytes; `load` is recreated every render, so it is
    // deliberately not a dependency.
  }, [key]);
  return url;
}

function LiveTheme({ effective, ns, onPreview }: { effective: EffectiveTheme | null; ns: string; onPreview: (s: PreviewSubject) => void }) {
  const identity = useIdentity();
  const logoUrl = useBlobUrl(
    effective?.logo_md5 ? () => effectiveThemeLogo(identity, ns) : null,
    effective ? `${effective.namespace}/${effective.name}/${effective.logo_md5 ?? ""}` : "none",
  );
  const backgroundUrl = useBlobUrl(
    effective?.background_md5 ? () => effectiveThemeBackground(identity, ns) : null,
    effective ? `${effective.namespace}/${effective.name}/background/${effective.background_md5 ?? ""}` : "none",
  );
  if (!effective) {
    return (
      <Alert severity="info">
        No theme is published for <b>{ns}</b> or any namespace above it. Guests see the portal's original look.
      </Alert>
    );
  }
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" } }}>
      {logoUrl && <Box component="img" src={logoUrl} alt="Published logo" sx={{ maxHeight: 64, maxWidth: 200, borderRadius: 1 }} />}
      <Stack spacing={1} sx={{ flex: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{effective.name}</Typography>
          <Chip size="small" color="success" label="Live" />
          {effective.inherited && <Chip size="small" variant="outlined" label={`inherited from ${effective.namespace}`} />}
        </Stack>
        <Swatches colours={[["ground", effective.ground], ["ink", effective.ink], ["accent", effective.accent]]} />
        {effective.inherited && (
          <Typography variant="body2" color="text.secondary">
            Your portal uses this theme until you publish one of your own.
          </Typography>
        )}
      </Stack>
      <Button
        variant="outlined"
        onClick={() => onPreview({
          name: `${effective.name} (live)`, ground: effective.ground, ink: effective.ink, accent: effective.accent, logoUrl,
          ...(effective.copy ? { copy: effective.copy } : {}),
          ...pageOf(effective), backgroundUrl,
        })}
      >
        Preview
      </Button>
    </Stack>
  );
}

function SavedThemeRow({ theme: t, ns, publishing, onPublish, onPreview, onEdit, onDelete }: {
  theme: SavedTheme;
  ns: string;
  publishing: boolean;
  onPublish: (name: string) => void;
  onPreview: (s: PreviewSubject) => void;
  onEdit: (s: EditorStart) => void;
  onDelete: (name: string) => void;
}) {
  const identity = useIdentity();
  const logoUrl = useBlobUrl(t.logo_md5 ? () => savedThemeLogo(identity, ns, t.name) : null, `${ns}/${t.name}/${t.logo_md5 ?? ""}`);
  const backgroundUrl = useBlobUrl(
    t.background_md5 ? () => savedThemeBackground(identity, ns, t.name) : null,
    `${ns}/${t.name}/background/${t.background_md5 ?? ""}`,
  );
  const tokens = deriveTokens(t.ground, t.ink, t.accent, t.mode);
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" }, py: 1 }}>
      {tokens && (
        <Box sx={{ width: { xs: "100%", sm: 200 }, flexShrink: 0 }}>
          <PortalPreview tokens={tokens} accent={t.accent} logoUrl={logoUrl} title="Your Wi-Fi" />
        </Box>
      )}
      <Stack spacing={0.5} sx={{ flex: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography sx={{ fontWeight: 600 }}>{t.name}</Typography>
          {t.published ? <Chip size="small" color="success" label="Live" /> : <Chip size="small" variant="outlined" label="Saved, not published" />}
          <Chip size="small" variant="outlined" label={t.mode} />
          {t.layout && t.layout !== "compact" && <Chip size="small" variant="outlined" label={t.layout === "wide" ? "wide card" : "full screen"} />}
          {t.background_md5 && <Chip size="small" variant="outlined" label="background picture" />}
        </Stack>
        <Swatches colours={[["ground", t.ground], ["ink", t.ink], ["accent", t.accent]]} />
        <Typography variant="caption" color="text.secondary">
          Updated {new Date(t.updated_at).toLocaleString()} by {t.updated_by}
        </Typography>
      </Stack>
      <Stack direction={{ xs: "row", sm: "column" }} spacing={1}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onPreview({ name: t.name, ground: t.ground, ink: t.ink, accent: t.accent, logoUrl, ...(t.copy ? { copy: t.copy } : {}), ...pageOf(t), backgroundUrl })}
        >
          Preview
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onEdit({
            name: t.name, ground: t.ground, ink: t.ink, accent: t.accent, copy: t.copy ?? {}, logo: null, unknown: [],
            backdrop: t.backdrop ?? "", layout: t.layout ?? "compact", cardOpacity: t.card_opacity ?? 100, logoSize: t.logo_size ?? "medium", background: null,
            storedLogo: t.logo_md5 ? () => savedThemeLogo(identity, ns, t.name) : null,
            storedBackground: t.background_md5 ? () => savedThemeBackground(identity, ns, t.name) : null,
          })}
        >
          Edit
        </Button>
        <Button variant="outlined" size="small" disabled={t.published || publishing} onClick={() => onPublish(t.name)}>
          {t.published ? "Live" : "Publish"}
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="error"
          disabled={t.published}
          title={t.published ? "This theme is live. Publish another theme before deleting it." : undefined}
          onClick={() => onDelete(t.name)}
        >
          Delete
        </Button>
      </Stack>
    </Stack>
  );
}

export function PortalAppearanceScreen() {
  const identity = useIdentity();
  const qc = useQueryClient();
  const ns = identity.namespace ?? "";

  const [logo, setLogo] = useState<LogoRead | null>(null);
  const [brand, setBrand] = useState<BrandColours | null>(null);
  const [chosen, setChosen] = useState("");
  const [shown, setShown] = useState(PAGE);
  const [name, setName] = useState("");
  const [readError, setReadError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [previewing, setPreviewing] = useState<PreviewSubject | null>(null);
  const [editing, setEditing] = useState<EditorStart | null>(null);
  const [fileProblems, setFileProblems] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const contact = usePortalContact(ns);
  const documents = useOperatorDocuments(ns);

  useEffect(() => () => { if (logo) URL.revokeObjectURL(logo.previewUrl); }, [logo]);

  const load = useQuery({
    queryKey: ["portalThemes", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: async () => (await listThemes(identity, ns)).data,
  });

  const variations = useMemo(() => (brand ? variationsFor(brand) : []), [brand]);
  const schemes = variations.slice(0, shown);
  const scheme = schemes.find((s) => s.id === chosen) ?? schemes[0] ?? null;
  const themeName = name.trim() || (scheme ? scheme.id : "");
  const themes = load.data?.themes ?? [];
  const existing = themes.find((t) => t.name === themeName);
  const atLimit = !existing && themes.length >= MAX_THEMES;
  const nameValid = THEME_NAME.test(themeName);

  const save = useMutation({
    mutationFn: async () => {
      if (!scheme || !logo) throw new ApiError("fail-closed", "Choose a logo and a scheme first.");
      return (await saveTheme(identity, ns, themeName, { ground: scheme.ground, ink: scheme.ink, accent: scheme.accent, logo: logo.base64 })).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portalThemes", ns] }),
  });

  const publish = useMutation({
    mutationFn: async (theme: string) => (await publishTheme(identity, ns, theme)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portalThemes", ns] }),
  });

  const remove = useMutation({
    mutationFn: async (theme: string) => (await deleteTheme(identity, ns, theme)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portalThemes", ns] }),
    onSettled: () => setConfirmDelete(null),
  });

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setReadError(null);
    setReading(true);
    save.reset();
    try {
      const read = await readLogo(file);
      setLogo(read);
      setBrand(extractBrand(read.sample.data, read.sample.width, read.sample.height));
      setChosen("");
      setShown(PAGE);
    } catch (err) {
      setReadError(err instanceof Error ? err.message : String(err));
    } finally {
      setReading(false);
    }
  }

  async function onThemeFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_THEME_FILE_BYTES) {
      setFileProblems([`The file is ${Math.round(file.size / 1024)} KB; a theme file is at most about 2 MB.`]);
      return;
    }
    const r = parseThemeFile(await file.text());
    if (!r.ok) {
      setFileProblems(r.problems);
      return;
    }
    setFileProblems([]);
    setEditing({ ...r.content, storedLogo: null, storedBackground: null, unknown: r.unknown });
  }

  function customise(s: { id: string; ground: string; ink: string; accent: string }) {
    if (!logo) return;
    const c = checkLogo(logo.base64);
    setEditing({
      name: s.id, ground: s.ground, ink: s.ink, accent: s.accent, copy: {}, ...PAGE_DEFAULTS,
      logo: "logo" in c ? c.logo : null, storedLogo: null, storedBackground: null, unknown: [],
    });
  }

  if (!ns) {
    return <Alert severity="error">No namespace could be resolved from your sign-in, so there is nothing to theme.</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 1100 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Portal appearance</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Your captive portal's colours and logo for <b>{ns}</b>. Save as many themes as you like and publish one — guests see the published theme the next time the portal loads, at the same address.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="overline" color="text.secondary">Live on your portal</Typography>
        {load.isLoading && <Typography variant="body2">Loading…</Typography>}
        {load.error && (
          <Alert severity="error">
            <AlertTitle>{describeError(load.error).title}</AlertTitle>
            {describeError(load.error).detail}
          </Alert>
        )}
        {load.data && <LiveTheme effective={load.data.effective} ns={ns} onPreview={setPreviewing} />}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="overline" color="text.secondary">New theme from your logo</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" }, mb: 2 }}>
          <Button variant="outlined" component="label" disabled={reading}>
            {reading ? "Reading…" : logo ? "Choose another logo" : "Choose logo"}
            <input hidden type="file" accept="image/png,image/jpeg" onChange={(e) => void onFile(e)} />
          </Button>
          <Typography variant="body2" color="text.secondary">PNG or JPEG. It is resized to fit 600×400 and 150 KB before upload.</Typography>
          <Box sx={{ flex: 1 }} />
          <Button variant="text" component="label">
            Open a theme file
            <input hidden type="file" accept="application/json,.json" onChange={(e) => void onThemeFile(e)} />
          </Button>
        </Stack>
        {readError && <Alert severity="error" sx={{ mb: 2 }}>{readError}</Alert>}
        {fileProblems.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFileProblems([])}>
            <AlertTitle>That file was not opened</AlertTitle>
            {fileProblems.map((p) => <div key={p}>{p}</div>)}
          </Alert>
        )}

        {logo && brand && (
          <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" }, mb: 2 }}>
              <Box component="img" src={logo.previewUrl} alt="Your logo" sx={{ maxHeight: 72, maxWidth: 220, borderRadius: 1, border: 1, borderColor: "divider" }} />
              <Stack spacing={1}>
                <Swatches colours={[["ink", brand.ink], ["accent", brand.accent], ["ground", brand.ground]]} />
                {!brand.chromatic && (
                  <Typography variant="body2" color="text.secondary">No colour was found in this logo, so the accent is AnkhOS amber. Pick another logo or use it as it is.</Typography>
                )}
                {brand.chromatic && brand.accentDerived && (
                  <Typography variant="body2" color="text.secondary">Your logo has one colour. The accent is a suggested complement, not taken from the logo.</Typography>
                )}
              </Stack>
            </Stack>

            <Box sx={{ display: "grid", gap: 2, mb: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" } }}>
              {schemes.map((s) => (
                <Paper
                  key={s.id}
                  variant="outlined"
                  role="button"
                  aria-pressed={s.id === scheme?.id}
                  tabIndex={0}
                  onClick={() => setChosen(s.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setChosen(s.id); }}
                  sx={{ p: 1.5, cursor: "pointer", borderWidth: 2, borderColor: s.id === scheme?.id ? "primary.main" : "divider" }}
                >
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{s.label}</Typography>
                    {s.id === scheme?.id && <Chip size="small" color="primary" label="Selected" />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>{s.note}</Typography>
                  <PortalPreview tokens={s.tokens} accent={s.accent} logoUrl={logo.previewUrl} title="Your Wi-Fi" />
                  <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                    Text {s.ratios.text.toFixed(1)}:1 · Button {s.ratios.button.toFixed(1)}:1 · Link {s.ratios.link.toFixed(1)}:1
                  </Typography>
                  {s.linkAdjusted && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      The accent alone reads {s.ratios.rawAccent.toFixed(1)}:1 as text, so links use a readable shade of it.
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      onKeyDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        setChosen(s.id);
                        setPreviewing({ name: s.label, ground: s.ground, ink: s.ink, accent: s.accent, logoUrl: logo.previewUrl });
                      }}
                    >
                      Full preview
                    </Button>
                    <Button
                      size="small"
                      onKeyDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        setChosen(s.id);
                        customise(s);
                      }}
                    >
                      Customise
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Box>
            <Stack direction="row" spacing={2} sx={{ alignItems: "center", mb: 3 }}>
              <Button
                variant="outlined"
                disabled={shown >= variations.length}
                onClick={() => setShown((n) => Math.min(variations.length, n + PAGE))}
              >
                More variations
              </Button>
              <Typography variant="body2" color="text.secondary">
                {shown >= variations.length
                  ? `All ${variations.length} variations for this logo are shown.`
                  : `Showing ${schemes.length} of ${variations.length} variations for this logo.`}
              </Typography>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "flex-start" } }}>
              <TextField
                size="small"
                label="Theme name"
                value={name}
                placeholder={scheme?.id ?? ""}
                onChange={(e) => setName(e.target.value)}
                error={!nameValid}
                helperText={
                  !nameValid
                    ? "1-64 letters, digits, '-' or '_'"
                    : existing?.published
                      ? `"${themeName}" is live: saving changes your portal on its next load.`
                      : existing
                        ? `Replaces the saved theme "${themeName}".`
                        : " "
                }
              />
              <Button
                variant="contained"
                disabled={!scheme || !nameValid || atLimit || save.isPending}
                onClick={() => save.mutate()}
              >
                {save.isPending ? "Saving…" : "Save theme"}
              </Button>
            </Stack>
            {atLimit && <Alert severity="warning" sx={{ mt: 2 }}>You already have {MAX_THEMES} saved themes. Save under an existing name to replace one.</Alert>}
            {save.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>{describeError(save.error).title}</AlertTitle>
                {describeError(save.error).detail}
              </Alert>
            )}
            {save.isSuccess && (
              <Alert
                severity="success"
                sx={{ mt: 2 }}
                action={
                  <Stack direction="row" spacing={1}>
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => setPreviewing({ name: save.data.name, ground: save.data.ground, ink: save.data.ink, accent: save.data.accent, logoUrl: logo.previewUrl })}
                    >
                      Preview
                    </Button>
                    {!save.data.published && (
                      <Button color="inherit" size="small" disabled={publish.isPending} onClick={() => publish.mutate(save.data.name)}>
                        Publish now
                      </Button>
                    )}
                  </Stack>
                }
              >
                Saved "{save.data.name}"{save.data.published ? " — it is live." : ". It is listed under Saved themes below."}
              </Alert>
            )}
          </>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="overline" color="text.secondary">Saved themes ({themes.length}/{MAX_THEMES})</Typography>
        {publish.error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            <AlertTitle>{describeError(publish.error).title}</AlertTitle>
            {describeError(publish.error).detail}
          </Alert>
        )}
        {publish.isSuccess && <Alert severity="success" sx={{ mb: 1 }}>Published "{publish.data.published}". Guests see it on their next portal load.</Alert>}
        {remove.error && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => remove.reset()}>
            <AlertTitle>{describeError(remove.error, "delete").title}</AlertTitle>
            {describeError(remove.error, "delete").detail}
          </Alert>
        )}
        {remove.isSuccess && <Alert severity="success" sx={{ mb: 1 }} onClose={() => remove.reset()}>Deleted "{remove.data.deleted}".</Alert>}
        {load.data && themes.length === 0 && <Typography variant="body2" color="text.secondary">No saved themes yet.</Typography>}
        <Stack divider={<Divider flexItem />} spacing={1}>
          {themes.map((t) => (
            <SavedThemeRow
              key={t.name}
              theme={t}
              ns={ns}
              publishing={publish.isPending}
              onPublish={(n) => publish.mutate(n)}
              onPreview={setPreviewing}
              onEdit={setEditing}
              onDelete={(n) => { remove.reset(); setConfirmDelete(n); }}
            />
          ))}
        </Stack>
      </Paper>

      <Dialog open={confirmDelete !== null} onClose={() => !remove.isPending && setConfirmDelete(null)}>
        <DialogTitle>Delete "{confirmDelete}"?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This removes the saved theme, its text and its logo from <b>{ns}</b>. It cannot be undone. Download the theme file from the editor first if you may want it back.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={remove.isPending} onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={remove.isPending} onClick={() => confirmDelete && remove.mutate(confirmDelete)}>
            {remove.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <PreviewDialog subject={previewing ? { ...previewing, contact, documents } : null} onClose={() => setPreviewing(null)} />
      <ThemeEditor start={editing} ns={ns} themes={themes} onClose={() => setEditing(null)} />
    </Box>
  );
}
